import React, {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useState,
} from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  Alert,
  FormControl,
  TextField,
  InputAdornment,
} from "@mui/material";
import { AttachMoney as AttachMoneyIcon } from "@mui/icons-material";
import { fetchBusiness } from 'modules/Business/actions';
import useBusinessFunding from "modules/Business/hooks/useBusinessFunding";
import {
  amountInputStep,
  getFundingAmountRules,
  validateConfiguredAmount,
} from "utils/businessPaymentRules";

const AddBalance = forwardRef(({ businessId, onClose, onFundSuccess }, ref) => {
  const dispatch = useDispatch();
  const [inputValue, setInputValue] = useState('');
  const [error, setError] = useState('');
  const {
    addFunds,
    clearError: clearFundingError,
    errorMessage: fundingError,
    isSubmitting,
    requiresPaymentReview,
  } = useBusinessFunding(businessId);

  const business = useSelector(state => state.business);

  useEffect(() => {
    dispatch(fetchBusiness(business.businessId, true));
  }, [dispatch, business.businessId]);

  const fundingRules = getFundingAmountRules(business);
  const [amount, setAmount] = useState(null);

  const handleChange = (e) => {
    const value = e.target.value;

    clearFundingError();
    setInputValue(value);
    const validation = validateConfiguredAmount(value, fundingRules);
    setError(validation.error);
    setAmount(validation.amount);
  };
  const handleSubmit = useCallback(async () => {
    const succeeded = await addFunds(amount);
    if (!succeeded) return false;
    if (onFundSuccess) onFundSuccess();
    if (onClose) onClose();
    return true;
  }, [addFunds, amount, onClose, onFundSuccess]);

  useImperativeHandle(ref, () => ({
    isSubmitting: () => isSubmitting,
    requiresPaymentReview: () => requiresPaymentReview,
    triggerFund: handleSubmit,
  }), [handleSubmit, isSubmitting, requiresPaymentReview]);

  return (
    <>
      <FormControl variant="outlined" fullWidth>
        <TextField
          label="Amount to add"
          variant="outlined"
          type="number"
          value={inputValue}
          onChange={handleChange}
          disabled={isSubmitting || requiresPaymentReview}
          error={!!error}
          helperText={error || ' '}
          fullWidth
          inputProps={{
            min: fundingRules?.minimumAmount,
            max: fundingRules?.maximumAmount,
            step: fundingRules ? amountInputStep(fundingRules.maximumScale) : undefined,
          }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <AttachMoneyIcon />
              </InputAdornment>
            ),
          }}
          sx={{
            '& .MuiInputBase-root': {
              height: '56px',
            },
            '& .MuiInputLabel-root': {
              top: '4px',
            },
          }}
        />
      </FormControl>
      {fundingError && (
        <Alert severity="error" sx={{ mt: 1 }}>
          {fundingError}
        </Alert>
      )}
    </>
  );
});

export default AddBalance;
